import { useId, useMemo, useState, type ChangeEvent } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import styles from "./CasinoBetsFilters.module.css";
import { Button, Collapse, Input, Select } from "../../../shared";
import type { BetFilterOption } from "../../../../queries/user-manager/useGetBetFilterDropdowns";
import { AppStrings } from "../../../../utils/constants/AppStrings";

export type CasinoBetsFiltersProps = {
  // Dropdown options
  statusOptions: BetFilterOption[];
  currencyOptions: BetFilterOption[];
  providerOptions: BetFilterOption[];
  gameOptions: BetFilterOption[];
  isLoadingGames?: boolean;

  // Draft state — what's selected in the dropdowns right now (pre-Apply).
  status: string[];
  moneyType: string;
  /** Provider LABEL (BE filters by sub_provider, not the raw value). */
  provider: string;
  /** Raw game id (option `value`). */
  gameId: string;
  searchValue: string;

  // Handlers
  onStatusChange: (v: string[] | undefined) => void;
  onMoneyTypeChange: (v: string | undefined) => void;
  onProviderChange: (v: string | undefined) => void;
  onGameChange: (v: string | undefined) => void;
  onSearchValueChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchPressed: () => void;
  onApply: () => void;
  onClear: () => void;

  /** Drives the Clear button's disabled state. */
  hasAppliedOrDraftFilters: boolean;
};

const CasinoBetsFilters = ({
  statusOptions,
  currencyOptions,
  providerOptions,
  gameOptions,
  isLoadingGames = false,
  status,
  moneyType,
  provider,
  gameId,
  searchValue,
  onStatusChange,
  onMoneyTypeChange,
  onProviderChange,
  onGameChange,
  onSearchValueChange,
  onSearchPressed,
  onApply,
  onClear,
  hasAppliedOrDraftFilters,
}: CasinoBetsFiltersProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const panelId = useId();

  // Use label as the antd Select value so what we store == what we send to BE.
  // This means a BE label wording change would silently break filters for existing sessions.
  // See CasinoBetsTable for label sync mitigation.
  const providerSelectOptions = useMemo(
    () => providerOptions.map((o) => ({ value: o.label, label: o.label })),
    [providerOptions],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <p className={styles.headerText}>{AppStrings.USER_CASINO_BETS}</p>

        <div className={styles.headerActions}>
          <Input
            variant="search"
            placeholder={AppStrings.SEARCH_BY_ID}
            className={styles.betSearchInput}
            value={searchValue}
            onChange={onSearchValueChange}
            data-testid="casino-bets-search-input"
          />

          <Button
            onClick={onSearchPressed}
            size="large"
            data-testid="casino-bets-search-button"
          >
            <SearchOutlined
              style={{
                fontSize: "20px",
                color: "var(--COLOR-LIGHT_TEXT_COLOR)",
              }}
            />
          </Button>

          <Button
            intent="primary"
            size="large"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-controls={panelId}
            data-testid="casino-bets-filters-toggle"
          >
            <span className={styles.filterToggleInner}>
              <Filter size={18} />
              {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          </Button>
        </div>
      </div>

      <Collapse open={open} className={styles.collapseHost}>
        <div
          id={panelId}
          className={styles.filtersPanel}
          data-testid="casino-bets-filters-panel"
        >
          <Select
            allowClear
            mode="multiple"
            className={styles.compactFilter}
            options={statusOptions}
            value={status}
            onChange={onStatusChange}
            placeholder={AppStrings.STATUS}
            data-testid="casino-bets-status-filter"
          />

          <Select
            allowClear
            className={styles.compactFilter}
            options={currencyOptions}
            value={moneyType || undefined}
            onChange={onMoneyTypeChange}
            placeholder={AppStrings.CURRENCY}
            data-testid="casino-bets-money-type-filter"
          />

          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            className={styles.compactFilter}
            options={providerSelectOptions}
            value={provider || undefined}
            onChange={onProviderChange}
            placeholder={AppStrings.PROVIDER}
            data-testid="casino-bets-provider-filter"
          />

          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            className={styles.compactFilter}
            options={gameOptions}
            value={gameId || undefined}
            onChange={onGameChange}
            placeholder={AppStrings.GAME}
            disabled={!provider}
            loading={isLoadingGames}
            data-testid="casino-bets-game-filter"
          />

          <Button
            size="large"
            onClick={onApply}
            data-testid="casino-bets-apply-filters"
          >
            {AppStrings.APPLY}
          </Button>

          <Button
            variant="ghost"
            size="large"
            onClick={onClear}
            disabled={!hasAppliedOrDraftFilters}
            data-testid="casino-bets-clear-filters"
          >
            {AppStrings.CLEAR_FILTERS}
          </Button>
        </div>
      </Collapse>
    </div>
  );
};

export default CasinoBetsFilters;
